import { ResponsiveBar } from "@nivo/bar";
import React, { useEffect, useMemo, useState } from "react";
import Text from "../../Tags/Text";
import relativeTime from "dayjs/plugin/relativeTime";
import dayjs from "dayjs";
import localizedFormat from "dayjs/plugin/localizedFormat";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import Image from "../../Tags/Image";
dayjs.extend(isSameOrBefore);
dayjs.extend(localizedFormat);
dayjs.extend(relativeTime);

const keys = ["contact", "company"];

export default function CreditUsageGraph({ sourceData, dateRange }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState([]);
  const [activeFilter, setActiveFilter] = useState({
    name: "Overview",
    value: "date",
  });
  const creditFilters = [
    {
      name: "Overview",
      value: "date",
    },
    {
      name: "By user",
      value: "name",
    },
  ];

  function getDailyOverviewData(records, dateRange) {
    const grouped = {};

    // Step 1: Aggregate actual data
    records.forEach((entry) => {
      const date = dayjs(entry.created_at).format("YYYY-MM-DD");

      if (!grouped[date]) {
        grouped[date] = {
          date,
          contact: 0,
          company: 0,
        };
      }

      if (entry.credit_type === "contact") {
        grouped[date].contact += entry.credits_consumed;
      } else if (entry.credit_type === "company") {
        grouped[date].company += entry.credits_consumed;
      }
    });

    if (records && records.length === 0) {
      return [];
    }

    // Step 2: Fill missing dates in the range
    const result = [];
    let current = dayjs(dateRange?.from_date);
    const end = dayjs(dateRange?.to_date);

    while (current.isSameOrBefore(end)) {
      const dateStr = current.format("YYYY-MM-DD");
      result.push(grouped[dateStr] || { date: dateStr, contact: 0, company: 0 });
      current = current.add(1, "day");
    }

    return result;
  }
  function getUserWiseChartData(records) {
    const grouped = {};

    records.forEach((entry) => {
      const user = entry.user;
      if (!user?.id) return;

      if (!grouped[user.id]) {
        grouped[user.id] = {
          id: user.id,
          name: user.name,
          email: user.email,
          contact: 0,
          company: 0,
        };
      }

      if (entry.credit_type === "contact") {
        grouped[user.id].contact += entry.credits_consumed;
      } else if (entry.credit_type === "company") {
        grouped[user.id].company += entry.credits_consumed;
      }
    });

    return Object.values(grouped);
  }

  useEffect(() => {
    setLoading(true);
    if (
      activeFilter &&
      activeFilter.value === "date" &&
      dateRange &&
      dateRange.from_date &&
      dateRange.to_date
    ) {
      setData(getDailyOverviewData(sourceData, dateRange));
      setLoading(false);
    }
    if (activeFilter && activeFilter.value === "name") {
      setData(getUserWiseChartData(sourceData));
      setLoading(false);
    }
  }, [activeFilter, dateRange]);

  const tickInterval = useMemo(() => {
    const totalPoints = data.length;
    if (totalPoints <= 15) return 1;
    if (totalPoints <= 30) return 3;
    if (totalPoints <= 90) return 7;
    return 30; // for a year, one tick per month
  }, [data]);

  const tickValues = useMemo(() => {
    return data.filter((_, index) => index % tickInterval === 0).map((d) => d.date);
  }, [data, tickInterval]);

  const tickFormat = useMemo(() => {
    const totalPoints = data.length;
    return (value) =>
      totalPoints > 90 ? dayjs(value).format("MMM YYYY") : dayjs(value).format("D MMM YYYY");
  }, [data]);

  return (
    <div>
      <div className="flex justify-between mb-2 mt-2">
        <Text size="lg">Credit usage</Text>
        <div className="inline-flex rounded-md border bg-background overflow-hidden">
          {creditFilters.map((item, index) => (
            <span
              onClick={() => {
                setActiveFilter(item);
              }}
              className={`${item.value === activeFilter?.value && "pointer-events-none"} py-3 font-bold cursor-pointer px-4 ${index + 1 < creditFilters.length ? "border-r" : ""} ${activeFilter?.value === item?.value ? "bg-gray-50" : ""}`}
            >
              {item?.name}
            </span>
          ))}
        </div>
      </div>
      {loading ? (
        <></>
      ) : (
        <>
          {data && data.length > 0 ? (
            <div style={{ height: 250 }}>
              <ResponsiveBar
                minValue={0}
                maxValue="auto"
                valueScale={{ type: "symlog", base: 10 }}
                data={data}
                keys={keys}
                indexBy={activeFilter?.value}
                margin={{ top: 20, right: 0, bottom: 40, left: 0 }}
                padding={0.3}
                innerPadding={2}
                groupMode="stacked"
                borderRadius={4}
                colors={["#7F56D9", "#D9BFFF"]} // Dark and light purple
                enableGridY={true}
                enableLabel={false}
                axisBottom={
                  activeFilter?.value === "date"
                    ? {
                        tickSize: 0,
                        tickPadding: 10,
                        tickValues,
                        format: tickFormat,
                      }
                    : {
                        tickSize: 0,
                        tickPadding: 10,
                        tickRotation: data?.length > 15 ? -45 : 0,
                        format: (value) =>
                          data?.length > 15 && value?.length > 8 ? value?.slice(0, 5) + "." : value,
                      }
                }
                axisLeft={null}
                tooltip={({ id, value, color, indexValue }) => (
                  <div
                    style={{
                      padding: "8px 12px",
                      background: "white",
                      border: "1px solid #E5E7EB",
                      borderRadius: "6px",
                      color: "#111827",
                      fontSize: "14px",
                    }}
                  >
                    <strong className="capitalize">{id}</strong>: {value} <br />
                    <span style={{ fontSize: "12px", color: "#6B7280" }}>{indexValue}</span>
                  </div>
                )}
                theme={{
                  tooltip: {
                    container: {
                      background: "white",
                      color: "#333",
                      fontSize: 12,
                    },
                  },
                  grid: {
                    line: {
                      stroke: "#706f6f0d",
                      strokeWidth: 1,
                    },
                  },
                }}
                isInteractive={true}
              />
            </div>
          ) : (
            <div className="text-center mb-6">
              <div className="inline-flex justify-center flex-col items-center">
                <Image src="/images/icons/untitledui/no-data-usage.svg" className="max-w-[200px]" />
                <p className="text-gray-600 text-lg">We couldn’t find anything here</p>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
